import { useState } from "react";
import { toast } from "sonner";
import { DatabaseBackup, Download, History } from "lucide-react";
import { api, errMsg, useApi } from "../lib/api";
import { BACKUP_TYPE, fmtBytes, fmtDate } from "../lib/labels";
import { PageHeader } from "../components/Layout";
import { EmptyRow } from "../components/Common";
import { BackupRestoreDialog } from "../components/BackupRestoreDialog";
import { Button } from "../components/ui/button";

const TypeTag = ({ type, testid }) => {
  const t = BACKUP_TYPE[type] || { label: type, cls: "border-slate-600 text-slate-300" };
  return <span data-testid={testid} className={`border px-2 py-0.5 font-mono text-[11px] uppercase tracking-widest ${t.cls}`}>{t.label}</span>;
};

const download = async (b) => {
  try {
    const { data, headers } = await api.get(`/admin/backups/${b.id}/download`, { responseType: "blob" });
    const name = /filename="([^"]+)"/.exec(headers["content-disposition"] || "")?.[1] || `dz-backup-${b.id}.json`;
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error(errMsg(e, "Não foi possível descarregar a cópia."));
  }
};

const Row = ({ b, onRestore }) => {
  const c = b.counts || {};
  return (
    <tr className="border-b border-slate-800/70 hover:bg-slate-900/60" data-testid={`backup-row-${b.id}`}>
      <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-slate-300">{fmtDate(b.created_at)}</td>
      <td className="px-4 py-3"><TypeTag type={b.type} testid={`backup-type-${b.id}`} /></td>
      <td className="px-4 py-3 text-xs text-slate-400">{b.created_by || "—"}</td>
      <td className="px-4 py-3 font-mono text-xs text-slate-300" data-testid={`backup-counts-${b.id}`}>
        {c.orgs ?? 0} / {c.zones ?? 0} / {c.recipes ?? 0} / {c.wars ?? 0}
      </td>
      <td className="px-4 py-3 font-mono text-xs text-slate-400">{fmtBytes(b.size)}</td>
      <td className="px-4 py-3 text-right">
        <Button size="sm" variant="ghost" onClick={() => download(b)} data-testid={`backup-download-${b.id}`} className="text-slate-300 hover:text-slate-50">
          <Download className="mr-1 h-3.5 w-3.5" />Descarregar (JSON)
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onRestore(b)} data-testid={`backup-restore-${b.id}`} className="text-rose-400 hover:text-rose-300">
          <History className="mr-1 h-3.5 w-3.5" />Restaurar
        </Button>
      </td>
    </tr>
  );
};

export default function BackupsPage() {
  const { data, reload } = useApi("/admin/backups", 60000);
  const [busy, setBusy] = useState(false);
  const [restoring, setRestoring] = useState(null);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/backups");
      toast.success("Cópia de segurança criada.");
      reload();
    } catch (e) {
      toast.error(errMsg(e, "Não foi possível criar a cópia."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Proteção de dados" title="Cópias de segurança">
        <Button onClick={create} disabled={busy} data-testid="backup-create-button" className="bg-emerald-500 text-slate-950 hover:bg-emerald-400">
          <DatabaseBackup className="mr-2 h-4 w-4" />{busy ? "A criar…" : "Criar cópia agora"}
        </Button>
      </PageHeader>
      <p className="fade-up mb-4 max-w-3xl text-sm text-slate-400" data-testid="backup-info">
        Guardadas na base de dados: automáticas diárias (últimas 7), manuais (últimas 10) e pré-restauro (últimas 5).
        Incluem organizações, zonas, receitas, configurações, temporadas, guerras e registo de crafts. Contas de admin e auditoria nunca são incluídas.
      </p>
      <div className="panel fade-up overflow-x-auto">
        <table className="w-full text-left" data-testid="backups-table">
          <thead className="border-b border-slate-800 font-mono text-[11px] uppercase tracking-widest text-slate-500">
            <tr><th className="px-4 py-3">Data</th><th className="px-4 py-3">Tipo</th><th className="px-4 py-3">Criada por</th><th className="px-4 py-3">Orgs / Zonas / Receitas / Guerras</th><th className="px-4 py-3">Tamanho</th><th className="px-4 py-3" /></tr>
          </thead>
          <tbody>
            {!data && <EmptyRow cols={6} text="A carregar…" testid="backups-loading" />}
            {data && data.items.length === 0 && <EmptyRow cols={6} text="Ainda não há cópias de segurança." testid="backups-empty" />}
            {(data?.items || []).map((b) => <Row key={b.id} b={b} onRestore={setRestoring} />)}
          </tbody>
        </table>
      </div>
      <BackupRestoreDialog backup={restoring} current={data?.current} onClose={() => setRestoring(null)}
        onDone={() => { setRestoring(null); reload(); }} />
    </div>
  );
}
